// PEOPLE-AVATARS pattern (extracted 2026-07-04 from PeoplePage/Directory.js so the PERSON-PROFILE
// header can reuse the SAME image-with-safety-net-or-initials logic instead of a parallel copy --
// DEV-WEB-STANDING-RULES 17b "reuse existing web components, invent nothing"). See the original
// fix's comment in Directory.js for the full root-cause writeup (legacy beta avatar uploads with
// absurd portrait-crop aspect ratios rendering as an unrecognizable colored smear, not a broken-
// image icon, so a plain onError handler alone never caught them).
//
// AVATAR-RENDER (2026-07-04, QJ repeated complaint -- the aspect-ratio-fallback pass above was
// NOT the fix he wanted): Sean Ballard's and Alessandra Franceschini's uploads both correctly trip
// `isDegenerateAvatarImg` (3.12:1 and 2.54:1, over the 2.2 threshold) and fell back to initials --
// safe, but wrong, because both are REAL photos of them sitting inside an oversized, mostly-blank-
// or-scenery padded canvas (a known beta upload quirk), not broken images. Initials-only hid a
// perfectly good photo. This adds a TRIM step before the fallback decision: ask the backend
// (GET /media/avatar-crop, see api/routers/media.py) for the near-uniform-padding content box --
// computed server-side because these cross-origin avatar URLs send no CORS header, so a client
// <canvas> pixel read would throw SecurityError. If a trim is found and the trimmed CONTENT's own
// aspect ratio is reasonable (<2:1), the image renders CROPPED to that content box (a real object-
// fit:cover-style crop restricted to the sub-rectangle, done via an explicit scale/translate since
// plain CSS object-fit only ever covers using the FULL natural image). Only when trimming still
// leaves an extreme ratio (or the backend found nothing to trim / errored) does it fall back to
// initials, same as before. Result cached in-memory per URL for the session (avoids re-fetching
// the same avatar's crop on every re-render across People rows / profile header).
import React, { useEffect, useRef, useState } from "react"

import { fetchAvatarCrop } from "@utils/devBackendShim"

const MAX_HEALTHY_ASPECT_RATIO = 2.2 // real profile photos run ~1:1 to ~3:4/4:3 (<=1.34);
// confirmed-broken beta uploads run 2.5-3.1 -- this sits safely between the two.
const MAX_HEALTHY_CONTENT_ASPECT_RATIO = 2.0 // threshold for the TRIMMED content box -- a
// landscape/vacation-style photo (Sean's, content_aspect ~0.9) still passes; only a still-extreme
// trim result (near-empty or genuinely panoramic content) falls through to initials.

export const isDegenerateAvatarImg = img => {
  const w = img.naturalWidth
  const h = img.naturalHeight
  if (!w || !h) return true
  return Math.max(w, h) / Math.min(w, h) > MAX_HEALTHY_ASPECT_RATIO
}

// Per-URL crop-lookup cache for the session (module-level -- shared across every PeopleAvatar
// instance so the same avatar rendered in multiple rows/places only ever fetches its crop once).
const cropCache = new Map()

const getCrop = async avatar => {
  if (cropCache.has(avatar)) return cropCache.get(avatar)
  const promise = fetchAvatarCrop(avatar).catch(() => ({ trimmed: false }))
  cropCache.set(avatar, promise)
  return promise
}

const isCropUsable = crop =>
  !!crop &&
  crop.trimmed &&
  crop.content_aspect &&
  Math.max(crop.content_aspect, 1 / crop.content_aspect) <= MAX_HEALTHY_CONTENT_ASPECT_RATIO

// `avatar` = image url (or falsy); `name` = source string for the initials fallback;
// `className` / `fallbackClassName` let callers size/place it (avatar frame sizing stays with
// the caller, e.g. the People directory's 36/56px rows vs the profile header's 82px circle, via
// an outer wrapper that gives this component's 100%-fill frame a definite box).
// `size` (px, optional): SIZE-CAP FIX (chain-4 U2, 2026-07-10, P1 visual defect). ROOT CAUSE:
// task/agenda-row callers pass their size directly as Tailwind h-*/w-* utilities on THIS
// component's own `className`, with no separately-sized wrapper ancestor -- but the frame `<div>`
// below also carries an inline `style={{width:"100%",height:"100%"}}`, and an inline style ALWAYS
// wins over a class-based rule for the same property in the CSS cascade, regardless of whether
// Tailwind's JIT generated the h-*/w-* utility for this build. So the className's intended size
// was silently discarded every time (component/build-path-level, not a missing-Tailwind-pipeline
// bug -- confirmed live: the picker popover's OTHER utilities, e.g. rounded-full/overflow-hidden/
// colors, all applied fine, and its rows sized correctly ONLY when rendering the initials-fallback
// `<span>` path, which carries no competing inline style). Compounding it, the `<img>` itself got
// NO sizing at all outside the rare cropped-and-usable path (`imgStyle` stays null for ordinary,
// non-degenerate photos), so with the frame's real box collapsed to "auto" (100% of an
// indeterminate ancestor resolves as content-driven), the unconstrained `<img>` rendered at its
// raw natural size (~300px) and the frame grew to match it -- `overflow:hidden` never got a
// definite box to clip against. Passing `size` hard-caps BOTH the frame (explicit px, beats any
// className/JIT-generation dependency) and the `<img>` (width/height 100% of that now-definite
// frame + belt-and-braces maxWidth/maxHeight in px), independent of Tailwind entirely. Callers
// that don't pass `size` keep the original width:100%/height:100%-of-ancestor behavior unchanged.
const PeopleAvatar = ({ avatar, name, className, fallbackClassName, size }) => {
  const [broken, setBroken] = useState(false)
  const [degenerate, setDegenerate] = useState(false)
  const [crop, setCrop] = useState(null) // {trimmed,top,bottom,left,right,width,height,content_aspect}
  const [imgStyle, setImgStyle] = useState(null)
  const frameRef = useRef(null)

  useEffect(() => {
    setBroken(false)
    setDegenerate(false)
    setCrop(null)
    setImgStyle(null)
  }, [avatar])

  // Only bother asking for a trim once the plain aspect-ratio check has flagged the image -- the
  // common case (an already-reasonable photo) never needs this extra round-trip.
  useEffect(() => {
    let cancelled = false
    if (degenerate && avatar) {
      getCrop(avatar).then(result => {
        if (!cancelled) setCrop(result)
      })
    }
    return () => {
      cancelled = true
    }
  }, [degenerate, avatar])

  // Compute the scale/translate that maps the trimmed content box onto the frame (an object-fit:
  // cover restricted to that sub-rectangle instead of the whole natural image).
  useEffect(() => {
    if (!isCropUsable(crop) || !frameRef.current) return
    const frame = frameRef.current
    const cw = frame.offsetWidth || 1
    const ch = frame.offsetHeight || 1
    const { width: w, height: h, top, bottom, left, right } = crop
    const cropW = (right - left) * w
    const cropH = (bottom - top) * h
    if (cropW <= 0 || cropH <= 0) return
    const scale = Math.max(cw / cropW, ch / cropH)
    const cx = (left + (right - left) / 2) * w
    const cy = (top + (bottom - top) / 2) * h
    setImgStyle({
      position: "absolute",
      width: w * scale,
      height: h * scale,
      left: cw / 2 - cx * scale,
      top: ch / 2 - cy * scale,
      maxWidth: "none",
      objectFit: "fill",
    })
  }, [crop])

  // Degenerate whole-image aspect ratio, and either no usable trim was found yet/at all, or the
  // trim itself is still extreme -> initials (same safety net as before this fix).
  const trimAttemptSettled = !degenerate || crop !== null
  const trimFailed = degenerate && trimAttemptSettled && !isCropUsable(crop)
  const stillBroken = broken || trimFailed
  const showImg = avatar && !stillBroken

  // Explicit-px frame size when `size` is given (belt: does not depend on the className/Tailwind
  // JIT path at all); otherwise the original fill-the-ancestor behavior.
  const frameSizeStyle = size
    ? { width: size, height: size, minWidth: size, minHeight: size }
    : { width: "100%", height: "100%" }
  // Default (non-cropped) image style: hard-caps the <img> to the frame's own box regardless of
  // the photo's natural intrinsic size, instead of the previous `undefined` (no sizing at all).
  // The cropped path (`imgStyle` set) already computes explicit px against the frame's REAL
  // measured `offsetWidth`/`offsetHeight`, so it is unaffected and takes precedence below.
  const defaultImgStyle = {
    width: "100%",
    height: "100%",
    ...(size ? { maxWidth: size, maxHeight: size } : {}),
    objectFit: "cover",
    display: "block",
  }

  return showImg ? (
    <div
      ref={frameRef}
      className={className}
      style={{ position: "relative", overflow: "hidden", flexShrink: 0, ...frameSizeStyle }}>
      <img
        src={avatar}
        alt=""
        style={imgStyle || defaultImgStyle}
        onError={() => setBroken(true)}
        onLoad={e => {
          if (isDegenerateAvatarImg(e.target)) setDegenerate(true)
        }}
      />
    </div>
  ) : (
    <span
      className={fallbackClassName || className}
      style={size ? { width: size, height: size, minWidth: size, minHeight: size } : undefined}>
      {(name || "?").slice(0, 1).toUpperCase()}
    </span>
  )
}

export default PeopleAvatar
